/**
 * Capitalizes the first letter of an error message
 * @param errorMessage - The error message to capitalize
 * @returns The error message with the first letter capitalized, or empty string if input is null/undefined
 */
export function capitalizeError(errorMessage: string | null | undefined): string {
  if (!errorMessage) return '';
  if (errorMessage.length === 0) return '';
  return errorMessage.charAt(0).toUpperCase() + errorMessage.slice(1);
}

/** Shown when the account token balance is too low to run optimization. */
export const OPTIMIZATION_INSUFFICIENT_TOKENS_USER_MESSAGE =
  'Not enough tokens on your account to run optimization. Add tokens in your profile or ask an administrator, then try again.';

/** Shown when Auto Fine-Tuning fails due to OpenAI billing/quota limits. */
export const FINE_TUNING_OPENAI_QUOTA_USER_MESSAGE =
  'Auto Fine-Tuning could not be completed. OpenAI API quota exceeded or no billing limit is available. Check your billing settings and API key, then try again.';

const FINE_TUNING_PROVIDER_GENERIC_USER_MESSAGE =
  'Fine-tuning could not be completed. An external AI provider error occurred. Check your API key, billing, and quota settings, then try again.';

const FINE_TUNING_TIMEOUT_USER_MESSAGE =
  'Fine-tuning could not be completed because the request timed out. Your integration may be slow or temporarily unavailable. Try again in a few minutes.';

const FINE_TUNING_CONNECTOR_USER_MESSAGE =
  'Fine-tuning could not be started because the system could not reach your integration. Check that the integration URL is correct and the service is running, then try again.';

const FINE_TUNING_GENERIC_USER_MESSAGE =
  'Fine-tuning could not be completed. Try again later or contact support if the problem persists.';

function looksLikeTechnicalFineTuningInfrastructureError(text: string): boolean {
  const lower = text.toLowerCase();
  return (
    lower.includes('context deadline exceeded') ||
    lower.includes('deadline exceeded') ||
    lower.includes('i/o timeout') ||
    lower.includes('connection timed out') ||
    lower.includes('connection refused') ||
    lower.includes('connection reset') ||
    lower.includes('connector request failed') ||
    lower.includes('failed to start external ft job') ||
    lower.includes('failed to start external') ||
    lower.includes('external ft job') ||
    /\/ft\/jobs\//i.test(text) ||
    /\b(post|get|put|patch|delete)\s+["'`/]/i.test(text) ||
    lower.includes('dial tcp') ||
    lower.includes('no such host') ||
    lower.includes('network is unreachable')
  );
}

function formatFineTuningInfrastructureErrorMessage(raw: string): string | null {
  const text = (raw || '').trim();
  if (!text) return null;
  const lower = text.toLowerCase();

  if (
    lower.includes('context deadline exceeded') ||
    lower.includes('deadline exceeded') ||
    lower.includes('i/o timeout') ||
    lower.includes('connection timed out')
  ) {
    return FINE_TUNING_TIMEOUT_USER_MESSAGE;
  }

  if (
    lower.includes('connector request failed') ||
    lower.includes('failed to start external ft job') ||
    lower.includes('failed to start external') ||
    lower.includes('external ft job') ||
    /\/ft\/jobs\//i.test(text)
  ) {
    return FINE_TUNING_CONNECTOR_USER_MESSAGE;
  }

  if (looksLikeTechnicalFineTuningInfrastructureError(text)) {
    return FINE_TUNING_GENERIC_USER_MESSAGE;
  }

  return null;
}

function looksLikeTechnicalFineTuningError(text: string): boolean {
  return looksLikeTechnicalProviderError(text) || looksLikeTechnicalFineTuningInfrastructureError(text);
}

function looksLikeTechnicalProviderError(text: string): boolean {
  const lower = text.toLowerCase();
  return (
    lower.includes('error code:') ||
    lower.includes('platform.openai.com') ||
    lower.includes('failed after') ||
    lower.includes('failed to regenerate responses') ||
    (lower.includes('type:') && lower.includes('code:')) ||
    /https?:\/\//.test(text)
  );
}

/** HTTP/SDK/JSON noise from dataset import jobs — hide from user-facing copy. */
function looksLikeTechnicalDatasetImportError(text: string): boolean {
  const trimmed = (text || '').trim();
  if (!trimmed) return false;
  const lower = trimmed.toLowerCase();
  return (
    lower.includes('connector error') ||
    lower.includes('sdk request failed') ||
    /\bstatus\s*[:=]?\s*\d{3}\b/.test(lower) ||
    /^sdk\b/.test(lower) ||
    trimmed.startsWith('{') ||
    trimmed.startsWith('[') ||
    lower.includes('"detail"') ||
    looksLikeTechnicalProviderError(trimmed)
  );
}

/**
 * Maps OpenAI / provider API failures embedded in fine-tuning job errors to actionable UI copy.
 * Returns null when no known provider pattern is detected.
 */
export function formatFineTuningProviderErrorMessage(raw: string): string | null {
  const text = (raw || '').trim();
  if (!text) return null;

  const infrastructureMessage = formatFineTuningInfrastructureErrorMessage(text);
  if (infrastructureMessage) return infrastructureMessage;

  const lower = text.toLowerCase();

  if (
    lower.includes('insufficient_quota') ||
    lower.includes('exceeded your current quota') ||
    lower.includes('exceeded the current quota') ||
    (lower.includes('quota') && (lower.includes('exceeded') || lower.includes(' exhausted')))
  ) {
    return FINE_TUNING_OPENAI_QUOTA_USER_MESSAGE;
  }

  if (
    lower.includes('invalid_api_key') ||
    lower.includes('incorrect api key') ||
    lower.includes('invalid api key') ||
    (lower.includes('authentication') && lower.includes('api'))
  ) {
    return 'Auto Fine-Tuning could not be completed. The OpenAI API key is invalid or not configured. Check your API key settings and try again.';
  }

  if (
    lower.includes('rate_limit_exceeded') ||
    lower.includes('rate limit') ||
    (lower.includes('error code: 429') && !lower.includes('quota'))
  ) {
    return 'Auto Fine-Tuning could not be completed. OpenAI API rate limit reached. Wait a few minutes and try again.';
  }

  if (lower.includes('error code: 401') || lower.includes('unauthorized')) {
    return 'Auto Fine-Tuning could not be completed. OpenAI API authentication failed. Check your API key settings and try again.';
  }

  if (lower.includes('error code: 403') || lower.includes('permission denied')) {
    return 'Auto Fine-Tuning could not be completed. OpenAI API access denied. Check your account permissions and API key, then try again.';
  }

  if (
    lower.includes('error code: 500') ||
    lower.includes('error code: 502') ||
    lower.includes('error code: 503') ||
    lower.includes('error code: 504')
  ) {
    return 'Auto Fine-Tuning could not be completed. OpenAI API is temporarily unavailable. Try again later.';
  }

  if (looksLikeTechnicalFineTuningError(text)) {
    return FINE_TUNING_PROVIDER_GENERIC_USER_MESSAGE;
  }

  return null;
}

export function isFineTuningProviderLevelError(raw: string): boolean {
  return formatFineTuningProviderErrorMessage(raw) != null;
}

export interface OptimizationErrorContext {
  previewDatasetTokens?: number | null;
}

export interface OptimizationInsufficientDatasetTokensDetails {
  requiredTokens?: number;
  foundTokens?: number;
}

const DATASET_TOKEN_COUNT_KEYS_REQUIRED = [
  'min_tokens',
  'minimum_tokens',
  'required_tokens',
  'min_token_count',
  'minimum_token_count',
  'required_token_count',
  'tokens_required',
  'min_dataset_tokens',
  'minimum_dataset_tokens',
] as const;

const DATASET_TOKEN_COUNT_KEYS_FOUND = [
  'found_tokens',
  'actual_tokens',
  'available_tokens',
  'dataset_tokens',
  'current_tokens',
  'token_count',
  'tokens_found',
  'present_tokens',
  'tokens_available',
  'total_tokens',
] as const;

function readNumericTokenCount(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  const n = typeof value === 'string' ? Number(value.replace(/,/g, '')) : Number(value);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return Math.round(n);
}

function readTokenCountFromRecord(record: Record<string, unknown>, keys: readonly string[]): number | undefined {
  for (const key of keys) {
    const value = record[key];
    const parsed = readNumericTokenCount(value);
    if (parsed != null) return parsed;
  }
  return undefined;
}

function collectOptimizationErrorObjects(raw: unknown): Record<string, unknown>[] {
  const result: Record<string, unknown>[] = [];
  const seen = new Set<unknown>();

  const visit = (value: unknown, depth: number) => {
    if (value == null || depth > 6) return;
    if (typeof value === 'object' && seen.has(value)) return;
    if (typeof value === 'object') seen.add(value);

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
          visit(JSON.parse(trimmed), depth + 1);
        } catch {
          // ignore invalid JSON fragments
        }
      }
      return;
    }

    if (typeof value !== 'object' || Array.isArray(value)) return;

    result.push(value as Record<string, unknown>);
    const obj = value as Record<string, unknown>;
    for (const key of ['error', 'data', 'details', 'detail', 'payload'] as const) {
      visit(obj[key], depth + 1);
    }
  };

  visit(raw, 0);
  if (raw instanceof Error) {
    visit(raw.message, 1);
    const payload = (raw as Error & { optimizationErrorPayload?: unknown }).optimizationErrorPayload;
    if (payload) visit(payload, 1);
  }
  return result;
}

function parseTokenCountsFromMessage(text: string): OptimizationInsufficientDatasetTokensDetails | null {
  const normalized = text.trim();
  if (!normalized) return null;

  const patterns: Array<{ re: RegExp; requiredIndex: 1 | 2; foundIndex: 1 | 2 }> = [
    {
      re: /(?:need|needs|require|requires|required|minimum|min(?:imum)?|at least)\s*(?:[:=]?\s*)?([\d,]+)\s*(?:tokens?)?[\s,;.-]*(?:found|got|have|available|present|contains?|is|are)\s*(?:[:=]?\s*)?([\d,]+)/i,
      requiredIndex: 1,
      foundIndex: 2,
    },
    {
      re: /(?:found|got|have|available|present|contains?)\s*(?:[:=]?\s*)?([\d,]+)\s*(?:tokens?)?[\s,;.-]*(?:need|needs|require|requires|required|minimum|min(?:imum)?|at least)\s*(?:[:=]?\s*)?([\d,]+)/i,
      requiredIndex: 2,
      foundIndex: 1,
    },
    {
      re: /required\s*[=:]\s*([\d,]+).*?(?:found|available|actual|present)\s*[=:]\s*([\d,]+)/i,
      requiredIndex: 1,
      foundIndex: 2,
    },
    {
      re: /(?:found|available|actual|present)\s*[=:]\s*([\d,]+).*?required\s*[=:]\s*([\d,]+)/i,
      requiredIndex: 2,
      foundIndex: 1,
    },
    {
      re: /minimum\s*[=:]\s*([\d,]+).*?(?:found|available|actual|present)\s*[=:]\s*([\d,]+)/i,
      requiredIndex: 1,
      foundIndex: 2,
    },
  ];

  for (const { re, requiredIndex, foundIndex } of patterns) {
    const match = normalized.match(re);
    if (!match) continue;
    const requiredTokens = readNumericTokenCount(match[requiredIndex]);
    const foundTokens = readNumericTokenCount(match[foundIndex]);
    if (requiredTokens != null || foundTokens != null) {
      return {
        requiredTokens: requiredTokens ?? undefined,
        foundTokens: foundTokens ?? undefined,
      };
    }
  }

  return null;
}

export function parseOptimizationInsufficientDatasetTokens(
  raw: unknown
): OptimizationInsufficientDatasetTokensDetails | null {
  for (const record of collectOptimizationErrorObjects(raw)) {
    const requiredTokens = readTokenCountFromRecord(record, DATASET_TOKEN_COUNT_KEYS_REQUIRED);
    const foundTokens = readTokenCountFromRecord(record, DATASET_TOKEN_COUNT_KEYS_FOUND);
    if (requiredTokens != null || foundTokens != null) {
      return {
        requiredTokens: requiredTokens ?? undefined,
        foundTokens: foundTokens ?? undefined,
      };
    }
  }

  return parseTokenCountsFromMessage(flattenOptimizationErrorText(raw));
}

function readOptimizationErrorCode(raw: unknown): string {
  for (const record of collectOptimizationErrorObjects(raw)) {
    const code = record.code;
    if (typeof code === 'string' && code.trim()) return code.trim().toLowerCase();
    if (typeof code === 'number') return String(code);
    const err = record.error;
    if (err && typeof err === 'object') {
      const nestedCode = (err as Record<string, unknown>).code;
      if (typeof nestedCode === 'string' && nestedCode.trim()) return nestedCode.trim().toLowerCase();
    }
  }
  return '';
}

function readHttpErrorStatus(raw: unknown): number | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const row = raw as Record<string, unknown>;
  const response = row.response;
  if (response && typeof response === 'object') {
    const status = (response as Record<string, unknown>).status;
    if (typeof status === 'number' && Number.isFinite(status)) return status;
  }
  const status = row.status;
  if (typeof status === 'number' && Number.isFinite(status)) return status;
  return undefined;
}

/** Collect nested API/axios/error payloads for optimization error formatting. */
function resolveOptimizationErrorSources(raw: unknown): unknown[] {
  const sources: unknown[] = [];
  const seen = new Set<unknown>();
  const push = (value: unknown) => {
    if (value == null || seen.has(value)) return;
    seen.add(value);
    sources.push(value);
  };

  push(raw);
  if (raw instanceof Error) {
    push(raw.message);
    push((raw as Error & { optimizationErrorPayload?: unknown }).optimizationErrorPayload);
    const axios = raw as Error & { response?: { status?: number; data?: unknown } };
    push(axios.response?.data);
    push(axios.response);
  }
  if (raw && typeof raw === 'object') {
    const row = raw as Record<string, unknown>;
    push(row.error);
    push(row.data);
    push(row.response);
    if (row.response && typeof row.response === 'object') {
      push((row.response as Record<string, unknown>).data);
    }
  }
  return sources;
}

function hasDatasetScopedTokenErrorText(text: string): boolean {
  const lower = text.toLowerCase();
  return /(dataset|training|dialog|example)/.test(lower) || /(required|found|need|at least|minimum)/.test(lower);
}

export function isAccountBalanceInsufficientTokensError(raw: unknown): boolean {
  if (readHttpErrorStatus(raw) === 402) return true;

  const code = readOptimizationErrorCode(raw);
  if (code === '402' || code === 'payment_required' || code === 'insufficient_funds') {
    return true;
  }

  const text = flattenOptimizationErrorText(raw).toLowerCase();
  if (!text) return false;
  if (/\b402\b/.test(text)) return true;
  if (
    /token_balance|token balance|insufficient balance|low balance|balance is too low|out of tokens|payment required|payment_required|insufficient_funds|account balance|billing|top up|add tokens to your account/i.test(
      text
    )
  ) {
    return true;
  }
  if (/not enough tokens on your account|tokens on your account/i.test(text)) return true;

  if (/insufficient tokens?/.test(text) && !hasDatasetScopedTokenErrorText(text)) {
    return true;
  }

  return false;
}

function isDatasetImportExamplesError(raw: unknown): boolean {
  const text = flattenOptimizationErrorText(raw).toLowerCase();
  return text.includes('not enough dataset examples');
}

export function isInsufficientDatasetTokensForOptimizationError(raw: unknown): boolean {
  if (isDatasetImportExamplesError(raw)) return false;
  if (isAccountBalanceInsufficientTokensError(raw)) return false;

  const code = readOptimizationErrorCode(raw);
  if (code === '402') return false;

  if (
    code === 'insufficient_dataset_tokens' ||
    code === 'insufficient_training_tokens' ||
    code === 'not_enough_dataset_tokens'
  ) {
    return true;
  }

  if (code === 'insufficient_tokens') {
    return (
      parseOptimizationInsufficientDatasetTokens(raw) != null ||
      hasDatasetScopedTokenErrorText(flattenOptimizationErrorText(raw))
    );
  }

  const text = flattenOptimizationErrorText(raw).toLowerCase();
  if (!text) return false;

  if (parseOptimizationInsufficientDatasetTokens(raw)) return true;

  if (/not enough/.test(text) && /(dataset|training|dialog|example|data)/.test(text)) return true;
  if (/not enough/.test(text) && /tokens?/.test(text) && /(required|found|need|at least|minimum)/.test(text)) {
    return true;
  }

  return false;
}

export function formatInsufficientDatasetTokensMessage(
  raw: unknown,
  context?: OptimizationErrorContext
): string | null {
  if (!isInsufficientDatasetTokensForOptimizationError(raw)) return null;

  const parsed = parseOptimizationInsufficientDatasetTokens(raw);
  const requiredTokens = parsed?.requiredTokens;
  const foundTokens =
    parsed?.foundTokens ??
    (context?.previewDatasetTokens != null && context.previewDatasetTokens >= 0
      ? Math.round(context.previewDatasetTokens)
      : undefined);

  const lines = ['Not enough training data to start Config Tuning.'];

  if (requiredTokens != null && foundTokens != null) {
    lines.push(
      `At least ${requiredTokens.toLocaleString()} tokens are required; the selected datasets contain ${foundTokens.toLocaleString()} tokens.`
    );
  } else if (requiredTokens != null) {
    lines.push(`At least ${requiredTokens.toLocaleString()} tokens are required in the selected datasets.`);
  } else if (foundTokens != null) {
    lines.push(
      `The selected datasets contain ${foundTokens.toLocaleString()} tokens, which is not enough to start Config Tuning.`
    );
  }

  lines.push('Select additional datasets or increase the amount of training data, then try again.');
  return lines.join(' ');
}

export function formatOptimizationJobStartError(
  raw: unknown,
  fallbackMessage: string = 'Failed to start optimization. Please try again.',
  context?: OptimizationErrorContext
): string {
  const sources = resolveOptimizationErrorSources(raw);

  for (const source of sources) {
    if (isAccountBalanceInsufficientTokensError(source)) {
      return OPTIMIZATION_INSUFFICIENT_TOKENS_USER_MESSAGE;
    }
  }

  for (const source of sources) {
    const datasetMessage = formatInsufficientDatasetTokensMessage(source, context);
    if (datasetMessage) return datasetMessage;
  }

  for (const source of sources) {
    const extracted = extractHttpErrorBodyMessage(source);
    if (extracted) {
      return formatJobErrorMessage(extracted, fallbackMessage);
    }
  }

  const message = getErrorMessage(raw, fallbackMessage);
  return formatJobErrorMessage(message, fallbackMessage);
}

function flattenOptimizationErrorText(raw: unknown): string {
  if (raw == null) return '';
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (t.length >= 2 && t[0] === '{' && t[t.length - 1] === '}') {
      try {
        return flattenOptimizationErrorText(JSON.parse(t));
      } catch {
        return raw;
      }
    }
    return raw;
  }
  if (typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    for (const key of ['message', 'detail', 'msg', 'description'] as const) {
      const v = o[key];
      if (typeof v === 'string' && v.trim()) return v;
    }
    const err = o.error;
    if (typeof err === 'string' && err.trim()) return err;
    if (err && typeof err === 'object') {
      const em = (err as Record<string, unknown>).message;
      if (typeof em === 'string' && em.trim()) return em;
      const c = (err as Record<string, unknown>).code;
      if (typeof c === 'string' && c.trim()) return c;
    }
    const code = o.code;
    if (typeof code === 'string' && code.trim()) return code;
    const data = o.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (data && typeof data === 'object') {
      const nested = flattenOptimizationErrorText(data);
      if (nested) return nested;
    }
    try {
      return JSON.stringify(raw);
    } catch {
      return String(raw);
    }
  }
  return String(raw);
}

export function isInsufficientTokensOptimizationError(raw: unknown): boolean {
  return isAccountBalanceInsufficientTokensError(raw) || isInsufficientDatasetTokensForOptimizationError(raw);
}

/** Maps WebSocket `onError` payloads and similar to a short UI string (token balance vs generic). */
export function formatOptimizationWebSocketError(
  raw: unknown,
  fallbackMessage: string = 'Optimization failed. Please try again.'
): string {
  const sources = resolveOptimizationErrorSources(raw);

  for (const source of sources) {
    if (isAccountBalanceInsufficientTokensError(source)) {
      return OPTIMIZATION_INSUFFICIENT_TOKENS_USER_MESSAGE;
    }
  }

  for (const source of sources) {
    const datasetMessage = formatInsufficientDatasetTokensMessage(source);
    if (datasetMessage) return datasetMessage;
  }

  for (const source of sources) {
    const extracted = extractHttpErrorBodyMessage(source);
    if (extracted) {
      return formatJobErrorMessage(extracted, fallbackMessage);
    }
  }

  const message = getErrorMessage(raw, fallbackMessage);
  return formatJobErrorMessage(message, fallbackMessage);
}

/**
 * Extracts a user-friendly error message from an axios error or any error object
 * @param error - The error object (can be axios error, Error, or any)
 * @param defaultMessage - Default message if no specific error can be extracted
 * @returns A user-friendly error message
 */
export function extractJobRawError(
  item: { error?: unknown; last_error?: unknown; status_reason?: unknown } | null | undefined,
  fallback = 'Job failed'
): string {
  if (!item) return fallback;
  for (const key of ['error', 'last_error', 'status_reason'] as const) {
    const value = item[key];
    const normalized = normalizeJobErrorText(value, '');
    if (normalized) return normalized;
  }
  return fallback;
}

export function normalizeJobErrorCompare(text: string): string {
  return text.trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Unwrap API/job error payloads that may be plain text, JSON strings, or `{ message }` objects. */
export function normalizeJobErrorText(raw: unknown, fallback = ''): string {
  if (raw == null) return fallback;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return fallback;
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed) as unknown;
        const extracted = extractHttpErrorBodyMessage(parsed);
        if (extracted) return extracted;
      } catch {
        // keep trimmed string
      }
    }
    return trimmed;
  }
  if (typeof raw === 'object') {
    const extracted = extractHttpErrorBodyMessage(raw);
    if (extracted) return extracted;
  }
  return fallback;
}

export function extractDatasetImportJobError(
  job: { error?: unknown; result?: unknown } | null | undefined,
  fallback = 'Failed to import dataset'
): string {
  if (!job) return fallback;
  const fromError = normalizeJobErrorText(job.error, '');
  if (fromError) return fromError;
  if (job.result && typeof job.result === 'object' && !Array.isArray(job.result)) {
    const result = job.result as Record<string, unknown>;
    const fromResult = normalizeJobErrorText(result.error ?? result.message, '');
    if (fromResult) return fromResult;
  }
  return fallback;
}

function formatDatasetExamplesShortageMessage(message: string): string | null {
  const m = normalizeJobErrorText(message, '');
  if (!m) return null;
  const examplesMatch = m.match(/not enough dataset examples:\s*got\s+(\d+),\s*need at least\s+(\d+)/i);
  if (examplesMatch) {
    return `Not enough dataset examples: found ${examplesMatch[1]}, need at least ${examplesMatch[2]}. Widen the date range or enable more signal types.`;
  }
  if (m.toLowerCase().includes('not enough dataset examples')) {
    return 'Not enough data to import. Widen the date range or enable more signal types.';
  }
  return null;
}

export function formatJobErrorMessage(raw: string, fallback = 'Job failed'): string {
  const m = normalizeJobErrorText(raw, '');
  if (!m) return fallback;

  const datasetExamplesMessage = formatDatasetExamplesShortageMessage(m);
  if (datasetExamplesMessage) return datasetExamplesMessage;

  const datasetMessage = formatInsufficientDatasetTokensMessage(m);
  if (datasetMessage) return datasetMessage;
  const lower = m.toLowerCase();

  if (lower.includes('no dialogs found')) {
    return 'No dialogs found for the selected period and filters. Widen the date range or change filters.';
  }
  if (lower.includes('need at least') && /got\s+\d+/.test(lower)) {
    return 'Not enough data to import. Widen the date range or enable more signal types.';
  }
  if (lower.includes('not enough train examples')) {
    return 'Not enough train examples. Increase the date range or adjust split ratio.';
  }
  if (lower.includes('not enough validation examples')) {
    return 'Not enough validation examples. Increase the date range or adjust split ratio.';
  }
  if (lower.includes('not enough messages with selected reactions')) {
    const zeroMatch = m.match(/got\s+(\d+)\s+reaction examples?/i);
    if (zeroMatch && zeroMatch[1] === '0') {
      return 'No messages match the selected reaction filters. Enable at least one reaction type or widen filters.';
    }
    return 'Not enough messages match the selected reaction filters. Enable more signal types or widen filters.';
  }
  if (lower.includes('failed to persist dataset in s3')) {
    return 'Failed to save the dataset to storage. Try again or contact support.';
  }
  if (lower.includes('failed to register dataset in connector')) {
    return 'Failed to register the dataset. Try again or contact support.';
  }
  if (lower.includes('at least one signal must be selected')) {
    return 'Select at least one filter: Like, Dislike, Comment, or Ground truth.';
  }
  if (
    lower.includes('selected dialogs have no messages matching selected dataset signals') ||
    lower.includes('no messages matching selected dataset signals')
  ) {
    return 'The selected dialogs have no messages matching the chosen signals. Try other dialogs, widen filters, or pick different users.';
  }
  if (lower.includes('virtual-user experiment failed') && m.length < 48) {
    return 'Virtual user experiment failed.';
  }
  if (
    (lower.includes('virtual-user dataset generation failed') ||
      lower.includes('virtual user dataset generation failed')) &&
    m.length < 56
  ) {
    return 'Virtual user dataset generation failed.';
  }

  return capitalizeError(m) || m;
}

/** User-facing copy for dataset import (import-jobs API). */
export function formatDatasetImportErrorMessage(serverMessage: unknown): string {
  const normalized = normalizeJobErrorText(serverMessage, '');
  const mapped = formatJobErrorMessage(normalized, 'Import failed. Please try again.');
  if (looksLikeTechnicalDatasetImportError(mapped) || looksLikeTechnicalDatasetImportError(normalized)) {
    return 'Import failed. Check your date range, filters, and signal selection, then try again.';
  }
  return mapped;
}

/** Friendly import error for a polled import job (no technical details). */
export function formatDatasetImportJobErrorMessage(
  job: { error?: unknown; result?: unknown } | null | undefined
): string {
  return formatDatasetImportErrorMessage(extractDatasetImportJobError(job));
}

/** Extract and format dataset import errors from API envelopes or Axios failures. */
export function resolveDatasetImportApiError(error: unknown, fallback = 'Failed to import dataset'): string {
  if (error != null && typeof error === 'object') {
    const maybeResponse = error as { response?: { data?: unknown } };
    if (maybeResponse.response?.data != null) {
      const msg = extractHttpErrorBodyMessage(maybeResponse.response.data);
      if (msg) return formatDatasetImportErrorMessage(msg);
    }
    const envelopeMsg =
      extractHttpErrorBodyMessage(error) || extractHttpErrorBodyMessage((error as { error?: unknown }).error);
    if (envelopeMsg) return formatDatasetImportErrorMessage(envelopeMsg);
  }
  return formatDatasetImportErrorMessage(getErrorMessage(error, fallback));
}

export interface JobErrorDisplay {
  raw: string;
  friendly: string;
  hasDetails: boolean;
  details?: string;
}

/** Shown when evaluation fails due to internal ML/backend errors. */
export const EVALUATION_GENERIC_USER_MESSAGE =
  'Evaluation could not be completed due to an internal error. Try again later or contact an administrator.';

const EVALUATION_TIMEOUT_USER_MESSAGE =
  'Evaluation could not be completed because the service timed out or was temporarily unavailable. Try again in a few minutes.';

function looksLikeTechnicalMlFrameworkError(text: string): boolean {
  const lower = text.toLowerCase();
  return (
    /unscale_\(\)/.test(lower) ||
    (/optimizer/.test(lower) && (/update\(\)/.test(lower) || /unscale_\(\)/.test(lower))) ||
    /[a-z_]+\(\) has already been called/.test(lower) ||
    lower.includes('traceback (most recent call last)') ||
    /\.py["'], line \d+/.test(text) ||
    /^\s*(runtime|attribute|type|value|index|key)error:/im.test(text) ||
    lower.includes('grad_scaler') ||
    (lower.includes('torch') && (lower.includes('error') || lower.includes('exception'))) ||
    lower.includes('pytorch') ||
    lower.includes('cuda error') ||
    lower.includes('cudnn') ||
    lower.includes('nccl') ||
    lower.includes('autograd') ||
    /module ['"`][^'"`]+['"`] has no attribute/.test(lower) ||
    lower.includes('stack trace')
  );
}

function looksLikeTechnicalEvaluationError(text: string): boolean {
  if (looksLikeTechnicalFineTuningInfrastructureError(text)) return true;
  return looksLikeTechnicalMlFrameworkError(text);
}

/** Maps evaluation job errors to user-facing copy; keeps actionable dataset/token messages. */
export function formatEvaluationJobErrorMessage(raw: string, fallback = 'Evaluation job failed.'): string {
  const m = normalizeJobErrorText(raw, '');
  if (!m) return fallback;

  if (isAccountBalanceInsufficientTokensError(m)) {
    return OPTIMIZATION_INSUFFICIENT_TOKENS_USER_MESSAGE;
  }

  const lower = m.toLowerCase();
  if (lower.includes('dataset not found')) {
    return 'The selected dataset was not found. Refresh the dataset list and try again.';
  }
  if (lower.includes('daily_rate_limit') || (lower.includes('rate limit') && lower.includes('exceeded'))) {
    return 'Evaluation rate limit reached. Please try again later.';
  }
  if (
    lower.includes('not enough') &&
    (lower.includes('dialog') || lower.includes('dataset') || lower.includes('example'))
  ) {
    return capitalizeError(m);
  }

  if (looksLikeTechnicalEvaluationError(m)) {
    if (looksLikeTechnicalFineTuningInfrastructureError(m)) {
      return EVALUATION_TIMEOUT_USER_MESSAGE;
    }
    return EVALUATION_GENERIC_USER_MESSAGE;
  }

  if (lower === 'evaluation failed' || lower === 'evaluation job failed') {
    return fallback;
  }

  return capitalizeError(m) || m;
}

export function resolveEvaluationJobError(
  job: { error?: unknown; last_error?: unknown; status_reason?: unknown } | null | undefined
): JobErrorDisplay {
  const raw = extractJobRawError(job, 'Evaluation job failed');
  const friendly = formatEvaluationJobErrorMessage(raw, 'Evaluation job failed.');
  const hasDetails = normalizeJobErrorCompare(raw) !== normalizeJobErrorCompare(friendly);
  return { raw, friendly, hasDetails, details: hasDetails ? raw : undefined };
}

export function resolveJobErrorDisplay(raw: string | null | undefined, fallback = 'Job failed'): JobErrorDisplay {
  const normalizedRaw = normalizeJobErrorText(raw, '') || fallback;
  const friendly = formatJobErrorMessage(normalizedRaw, fallback);
  const hasDetails = normalizeJobErrorCompare(normalizedRaw) !== normalizeJobErrorCompare(friendly);
  return { raw: normalizedRaw, friendly, hasDetails, details: normalizedRaw };
}

export function resolveOptimizationJobError(
  job: { error?: unknown; last_error?: unknown; status_reason?: unknown } | null | undefined
): JobErrorDisplay {
  const raw = extractJobRawError(job, 'Optimization job failed');
  const friendly =
    formatInsufficientDatasetTokensMessage(job) ??
    formatInsufficientDatasetTokensMessage(raw) ??
    resolveJobErrorDisplay(raw, 'Optimization job failed').friendly;
  const hasDetails = normalizeJobErrorCompare(raw) !== normalizeJobErrorCompare(friendly);
  return { raw, friendly, hasDetails, details: raw };
}

export function resolveFineTuningJobError(job: Record<string, unknown> | null | undefined): JobErrorDisplay {
  const fallback = 'Fine-tuning could not be completed.';
  if (!job) {
    return { raw: fallback, friendly: fallback, hasDetails: false, details: fallback };
  }
  const jobRaw = extractJobRawError(job as { error?: unknown }, '');

  const groups = (Array.isArray(job.groups) ? job.groups : []) as Array<{
    status?: string;
    error?: string;
    base_model?: string;
  }>;
  const agents = (Array.isArray(job.agents) ? job.agents : []) as Array<{
    status?: string;
    error?: string;
    agent_name?: string;
  }>;

  const failedGroupLines = groups
    .filter((g) => String(g.status || '').toLowerCase() === 'failed' && String(g.error || '').trim())
    .map((g) => String(g.error).trim());
  const failedAgentLines = agents
    .filter((a) => String(a.status || '').toLowerCase() === 'failed' && String(a.error || '').trim())
    .map((a) => {
      const name = String(a.agent_name || '').trim();
      const err = String(a.error || '').trim();
      if (name && !err.toLowerCase().includes(name.toLowerCase())) return `${name}: ${err}`;
      return err;
    });
  const failedAgentsField = (Array.isArray(job.failed_agents) ? job.failed_agents : []) as TrainingJobFailedAgentLike[];
  const failedAgentLinesFromField = failedAgentsField
    .filter((a) => String(a.error || '').trim())
    .map((a) => {
      const name = String(a.agent_name || '').trim();
      const err = String(a.error || '').trim();
      if (name && !err.toLowerCase().includes(name.toLowerCase())) return `${name}: ${err}`;
      return err;
    });

  const subRawLines = dedupeTrainingJobErrorLines([
    ...failedGroupLines,
    ...failedAgentLines,
    ...failedAgentLinesFromField,
  ]);
  const raw = jobRaw || subRawLines.join('\n') || fallback;

  const rawLines = dedupeTrainingJobErrorLines([...(jobRaw ? [jobRaw] : []), ...subRawLines]);
  let effectiveRawLines = rawLines;
  if (jobRaw && subRawLines.length > 0) {
    const jobFormatted = formatFineTuningJobErrorLine(jobRaw);
    const duplicatesSub = subRawLines.some((line) => trainingJobErrorsEquivalent(jobRaw, line));
    const subDuplicatesJob = subRawLines.some((line) =>
      trainingJobErrorsEquivalent(jobFormatted, formatFineTuningJobErrorLine(line))
    );
    if (duplicatesSub || subDuplicatesJob) {
      effectiveRawLines = dedupeTrainingJobErrorLines(subRawLines);
    }
  }

  const formattedLines = dedupeTrainingJobErrorLines(
    effectiveRawLines.map(formatFineTuningJobErrorLine).filter(Boolean)
  );
  const { primary, additional } = pickPrimaryUserFacingErrorMessage(formattedLines);

  let friendly = primary;
  if (!friendly) {
    friendly =
      formatFineTuningProviderErrorMessage(raw) ??
      (looksLikeTechnicalFineTuningError(raw) ? FINE_TUNING_GENERIC_USER_MESSAGE : fallback);
  } else if (
    looksLikeTechnicalFineTuningError(raw) &&
    normalizeJobErrorCompare(friendly) === normalizeJobErrorCompare(raw)
  ) {
    friendly = FINE_TUNING_GENERIC_USER_MESSAGE;
  }

  let details: string | undefined;
  let hasDetails = false;
  if (additional.length > 0) {
    details = additional.join('\n\n');
    hasDetails = true;
  } else {
    const formattedDetails = dedupeTrainingJobErrorLines(
      effectiveRawLines.map(formatFineTuningJobErrorLine).filter(Boolean)
    );
    const detailsWithoutPrimary = formattedDetails.filter((line) => !trainingJobErrorsEquivalent(line, friendly));
    if (detailsWithoutPrimary.length > 0) {
      details = detailsWithoutPrimary.join('\n\n');
      hasDetails = true;
    } else {
      const rawDetails = (effectiveRawLines.join('\n\n') || raw).trim();
      const formattedRaw = formattedDetails.join('\n\n').trim();
      const detailsCandidate = formattedRaw || rawDetails;
      hasDetails =
        Boolean(detailsCandidate) && normalizeJobErrorCompare(detailsCandidate) !== normalizeJobErrorCompare(friendly);
      details = hasDetails ? detailsCandidate : undefined;
    }
  }

  return { raw, friendly, hasDetails, details };
}

export function extractPipelineErrorMessage(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';

  const jsonObjectMatch = trimmed.match(/\{[\s\S]*"message"\s*:\s*"((?:\\.|[^"\\])*)"[\s\S]*\}/);
  if (jsonObjectMatch) {
    try {
      return JSON.parse(`"${jsonObjectMatch[1]}"`) as string;
    } catch {
      return jsonObjectMatch[1].replace(/\\"/g, '"');
    }
  }

  if (trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(trimmed) as { message?: unknown; error?: unknown };
      if (typeof parsed.message === 'string' && parsed.message.trim()) {
        return parsed.message.trim();
      }
      if (typeof parsed.error === 'string' && parsed.error.trim()) {
        return parsed.error.trim();
      }
    } catch {
      // fall through to line cleanup
    }
  }

  return trimmed
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => {
      if (!line) return false;
      const lower = line.toLowerCase();
      if (lower === 'bad request') return false;
      if (/^response status \d+$/i.test(line)) return false;
      if (line.startsWith('{') && line.endsWith('}')) return false;
      if (line.startsWith('[') && line.endsWith(']')) return false;
      return true;
    })
    .join(' ')
    .trim();
}

function looksLikeTechnicalPipelineError(message: string): boolean {
  const text = message.trim();
  if (!text) return false;
  if (text.startsWith('{') || text.startsWith('[')) return true;
  if (looksLikeTechnicalProviderError(text)) return true;

  const lower = text.toLowerCase();
  return (
    lower.includes('internal server error') ||
    lower.includes('axioserror') ||
    lower.includes('traceback') ||
    lower.includes('stack trace') ||
    lower.includes('runtimeerror') ||
    lower.includes('exception:') ||
    /^error code:\s*\d+/i.test(text) ||
    /^status code \d+/i.test(text)
  );
}

const PIPELINE_TASK_STEP_LABELS: Record<string, string> = {
  load_integration_datasets: 'Load Integration Datasets',
  generate_vuser_datasets: 'Generate Virtual User Datasets',
  select_training_datasets: 'Select Training Datasets',
  config_tuning: 'Config Tuning',
  fine_tuning: 'Fine Tuning',
  fine_tune: 'Fine Tuning',
};

function pipelineTaskStepLabel(taskType: string): string {
  const key = taskType.trim().toLowerCase();
  return PIPELINE_TASK_STEP_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Turns backend config-validation errors like
 * `Invalid load_integration_datasets task config` into user-facing copy.
 */
export function formatPipelineTaskConfigError(message: string): string | null {
  const trimmed = message.trim();
  if (!trimmed) return null;

  const patterns = [
    /invalid\s+([a-z_]+)\s+task\s+config/i,
    /([a-z_]+)\s+task\s+config\s+is\s+invalid/i,
    /invalid\s+task\s+config\s+for\s+([a-z_]+)/i,
  ];

  for (const pattern of patterns) {
    const match = trimmed.match(pattern);
    if (match) {
      const label = pipelineTaskStepLabel(match[1]);
      return `${label}: this step could not run because its configuration is invalid. Open the pipeline, review the ${label} step settings, and try again. If the problem persists, contact support.`;
    }
  }

  return null;
}

/** User-facing copy for pipeline cards, runs, and task steps. */
export function formatPipelinePeriodDaysConfigError(message: string): string | null {
  const trimmed = message.trim();
  if (!trimmed) return null;

  const periodDaysPattern =
    /(?:load_integration_datasets|select_training_datasets)\.period_days\s+must be a number between 1 and 365/i;
  const genericPattern = /period_days\s+must be a number between 1 and 365/i;

  if (periodDaysPattern.test(trimmed) || genericPattern.test(trimmed)) {
    const stepLabel = /load_integration_datasets/i.test(trimmed)
      ? 'Load Integration Datasets'
      : /select_training_datasets/i.test(trimmed)
        ? 'Select Training Datasets'
        : 'Pipeline step';
    return `${stepLabel}: Period days must be a whole number between 1 and 365. Edit the pipeline configuration and try again.`;
  }

  return null;
}

/** User-facing copy for pipeline cards, runs, and task steps. */
export function formatPipelineErrorMessage(
  raw: string | null | undefined,
  fallback = 'This pipeline step failed. Review the configuration and try again.'
): string {
  const message = extractPipelineErrorMessage((raw || '').trim());
  if (!message) return fallback;

  const compositeParts = splitCompositePipelineErrorMessage(message);
  if (compositeParts) {
    const formatted = compositeParts.map((part) => formatPipelineErrorMessage(part, fallback));
    return pickPrimaryUserFacingErrorMessage(formatted).primary || fallback;
  }

  const periodDaysError = formatPipelinePeriodDaysConfigError(message);
  if (periodDaysError) return periodDaysError;

  const taskConfigError = formatPipelineTaskConfigError(message);
  if (taskConfigError) return taskConfigError;

  const lower = message.toLowerCase();
  if (lower.includes('virtual user must have a persona')) {
    return 'A virtual user is missing a persona. Assign one in Virtual User Service, then try again.';
  }

  const groupedFineTuning = formatFineTuningGroupErrorMessage(message);
  if (normalizeJobErrorCompare(groupedFineTuning) !== normalizeJobErrorCompare(message)) {
    return groupedFineTuning;
  }

  const training = formatTrainingJobUserErrorMessage(message);
  if (normalizeJobErrorCompare(training) !== normalizeJobErrorCompare(message)) {
    return training;
  }

  const provider = formatFineTuningProviderErrorMessage(message);
  if (provider) return provider;

  if (
    (lower.includes('virtual-user dataset generation failed') ||
      lower.includes('virtual user dataset generation failed')) &&
    message.length < 56
  ) {
    return fallback;
  }

  const jobFormatted = formatJobErrorMessage(message, fallback);
  if (normalizeJobErrorCompare(jobFormatted) !== normalizeJobErrorCompare(message)) {
    return jobFormatted;
  }

  if (looksLikeTechnicalPipelineError(message)) {
    return fallback;
  }

  return jobFormatted;
}

function normalizePipelineTaskResult(result?: unknown): Record<string, unknown> | null {
  if (result == null) return null;
  if (typeof result === 'string') {
    const trimmed = result.trim();
    if (!trimmed) return null;
    try {
      const parsed = JSON.parse(trimmed) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return null;
    }
    return null;
  }
  if (typeof result === 'object' && !Array.isArray(result)) {
    return result as Record<string, unknown>;
  }
  return null;
}

function isGenericVuserDatasetFailureMessage(message: string): boolean {
  const normalized = normalizeJobErrorCompare(message);
  return (
    normalized === normalizeJobErrorCompare('Virtual user dataset generation failed') ||
    normalized === normalizeJobErrorCompare('virtual-user dataset generation failed') ||
    normalized === normalizeJobErrorCompare('Virtual user dataset generation failed.')
  );
}

function formatPipelineDetailLabel(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatPipelineDetailValue(value: unknown): string {
  if (value == null || value === '') return '—';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function extractPipelineTaskResultDetails(result?: Record<string, unknown> | null): Record<string, unknown> | null {
  const details = result?.details;
  if (!details || typeof details !== 'object' || Array.isArray(details)) return null;
  return details as Record<string, unknown>;
}

type PipelineFailedAgentLike = TrainingJobFailedAgentLike & { status?: string };

function collectPipelineTaskFailedAgents(
  result?: Record<string, unknown> | null,
  details?: Record<string, unknown> | null
): PipelineFailedAgentLike[] {
  const collected: PipelineFailedAgentLike[] = [];

  const pushUnique = (agent: PipelineFailedAgentLike) => {
    const name = String(agent.agent_name || '').trim();
    const err = String(agent.error || '').trim();
    if (!err) return;
    const key = `${name}\u0000${err}`;
    if (
      collected.some(
        (existing) => `${String(existing.agent_name || '').trim()}\u0000${String(existing.error || '').trim()}` === key
      )
    ) {
      return;
    }
    collected.push({ agent_name: name || undefined, error: err, status: agent.status });
  };

  const ingestArray = (value: unknown) => {
    if (!Array.isArray(value)) return;
    for (const item of value) {
      if (!item || typeof item !== 'object') continue;
      pushUnique(item as PipelineFailedAgentLike);
    }
  };

  ingestArray(details?.failed_agents);
  ingestArray(result?.failed_agents);

  const agents = result?.agents;
  if (Array.isArray(agents)) {
    for (const item of agents) {
      if (!item || typeof item !== 'object') continue;
      const row = item as PipelineFailedAgentLike;
      const status = String(row.status || '').toLowerCase();
      if (status && status !== 'failed') continue;
      pushUnique(row);
    }
  }

  return collected;
}

function formatPipelineFailedAgentsDetails(agents: PipelineFailedAgentLike[]): string | null {
  if (agents.length === 0) return null;
  const lines = dedupeTrainingJobErrorLines(agents.map(formatTrainingJobFailedAgentLine));
  return lines.length > 0 ? lines.join('\n\n') : null;
}

function buildFineTuningErrorJobFromTaskRun(taskRun: {
  error?: string | null;
  result?: unknown;
}): Record<string, unknown> {
  const result = normalizePipelineTaskResult(taskRun.result);
  const details = extractPipelineTaskResultDetails(result);
  const failedAgents = collectPipelineTaskFailedAgents(result, details);
  const taskError = (taskRun.error || '').trim();
  const resultError = typeof result?.error === 'string' ? result.error.trim() : '';
  const detailsReason = typeof details?.reason === 'string' ? details.reason.trim() : '';

  return {
    error: taskError || resultError || detailsReason || undefined,
    groups: result && Array.isArray(result.groups) ? result.groups : undefined,
    agents: result && Array.isArray(result.agents) ? result.agents : undefined,
    failed_agents: failedAgents.length > 0 ? failedAgents : undefined,
  };
}

function extractPipelineTaskRunRawError(taskRun: { error?: string | null; result?: unknown }): string {
  const result = normalizePipelineTaskResult(taskRun.result);

  const details = extractPipelineTaskResultDetails(result);
  if (details) {
    const reason = typeof details.reason === 'string' ? extractPipelineErrorMessage(details.reason) : '';
    if (reason) return reason;
  }

  if (result) {
    const resultError = typeof result.error === 'string' ? extractPipelineErrorMessage(result.error) : '';
    if (resultError) return resultError;
  }

  const direct = extractPipelineErrorMessage((taskRun.error || '').trim());
  if (direct) return direct;

  if (!result) return '';

  const reason = typeof result.reason === 'string' ? extractPipelineErrorMessage(result.reason) : '';
  if (reason) return reason;

  const groups = (Array.isArray(result.groups) ? result.groups : []) as Array<{ status?: string; error?: string }>;
  const agents = (Array.isArray(result.agents) ? result.agents : []) as Array<{
    status?: string;
    error?: string;
    agent_name?: string;
  }>;
  const failedAgents = collectPipelineTaskFailedAgents(result, details);

  const failedLines = [
    ...groups
      .filter((group) => String(group.status || '').toLowerCase() === 'failed' && String(group.error || '').trim())
      .map((group) => String(group.error).trim()),
    ...agents
      .filter((agent) => String(agent.status || '').toLowerCase() === 'failed' && String(agent.error || '').trim())
      .map((agent) => String(agent.error).trim()),
    ...failedAgents.map((agent) => {
      const name = String(agent.agent_name || '').trim();
      const err = String(agent.error || '').trim();
      if (name && !err.toLowerCase().includes(name.toLowerCase())) return `${name}: ${err}`;
      return err;
    }),
  ];

  return failedLines[0] || '';
}

function buildVuserDatasetFriendlyFromDetails(details: Record<string, unknown>): string | null {
  const reason = typeof details.reason === 'string' ? extractPipelineErrorMessage(details.reason.trim()) : '';
  if (reason && !isGenericVuserDatasetFailureMessage(reason)) {
    return capitalizeError(reason);
  }

  const summaryParts: string[] = [];
  if (typeof details.stage === 'string' && details.stage.trim()) {
    summaryParts.push(`Failed at stage: ${details.stage.trim()}`);
  }
  if (typeof details.experiment_status === 'string' && details.experiment_status.trim()) {
    summaryParts.push(`Experiment status: ${details.experiment_status.trim()}`);
  }
  if (typeof details.dataset_status === 'string' && details.dataset_status.trim()) {
    summaryParts.push(`Dataset status: ${details.dataset_status.trim()}`);
  }
  if (summaryParts.length > 0) {
    return summaryParts.join('. ');
  }

  if (reason) return capitalizeError(reason);
  return null;
}

function resolvePipelineTaskRunFriendlyMessage(
  taskRun: {
    type?: string;
    error?: string | null;
    result?: unknown;
  },
  fallback: string
): string {
  const type = String(taskRun.type || '').toLowerCase();
  const result = normalizePipelineTaskResult(taskRun.result);

  if (type === 'generate_vuser_datasets') {
    const details = extractPipelineTaskResultDetails(result);
    if (details) {
      const fromDetails = buildVuserDatasetFriendlyFromDetails(details);
      if (fromDetails) return fromDetails;
    }
  }

  if ((type === 'fine_tuning' || type === 'fine_tune') && result) {
    const ftDisplay = resolveFineTuningJobError({
      error: taskRun.error,
      groups: result.groups,
      agents: result.agents,
    });
    if (ftDisplay.friendly.trim()) {
      return ftDisplay.friendly;
    }
  }

  const raw = extractPipelineTaskRunRawError(taskRun);
  if (raw && type === 'generate_vuser_datasets' && !isGenericVuserDatasetFailureMessage(raw)) {
    return capitalizeError(raw);
  }
  return formatPipelineErrorMessage(raw, fallback);
}

export function buildPipelineTaskRunErrorDetails(taskRun: { error?: string | null; result?: unknown }): string | null {
  const parts: string[] = [];
  const result = normalizePipelineTaskResult(taskRun.result);
  const handledDetailKeys = new Set<string>();

  const pushDetail = (key: string, label: string, value: unknown) => {
    if (value == null || value === '') return;
    handledDetailKeys.add(key);
    parts.push(`${label}: ${formatPipelineDetailValue(value)}`);
  };

  const details = extractPipelineTaskResultDetails(result);
  const failedAgents = collectPipelineTaskFailedAgents(result, details);
  if (failedAgents.length > 0) {
    handledDetailKeys.add('failed_agents');
    handledDetailKeys.add('agents');
    const block = formatPipelineFailedAgentsDetails(failedAgents);
    if (block) parts.push(block);
  }

  if (details) {
    pushDetail('stage', 'Stage', details.stage);
    pushDetail('reason', 'Reason', details.reason);
    if (details.total_dialogs != null || details.completed_dialogs != null || details.failed_dialogs != null) {
      handledDetailKeys.add('total_dialogs');
      handledDetailKeys.add('completed_dialogs');
      handledDetailKeys.add('failed_dialogs');
      parts.push(
        `Dialogs: ${details.completed_dialogs ?? 0}/${details.total_dialogs ?? 0} completed` +
          (details.failed_dialogs != null ? `, ${details.failed_dialogs} failed` : '')
      );
    }
    if (details.total_messages != null || details.completed_messages != null || details.failed_messages != null) {
      handledDetailKeys.add('total_messages');
      handledDetailKeys.add('completed_messages');
      handledDetailKeys.add('failed_messages');
      parts.push(
        `Messages: ${details.completed_messages ?? 0}/${details.total_messages ?? 0} completed` +
          (details.failed_messages != null ? `, ${details.failed_messages} failed` : '')
      );
    }
    pushDetail('experiment_id', 'Experiment ID', details.experiment_id);
    pushDetail('experiment_status', 'Experiment status', details.experiment_status);
    pushDetail('dataset_status', 'Dataset status', details.dataset_status);

    for (const [key, value] of Object.entries(details)) {
      if (handledDetailKeys.has(key)) continue;
      pushDetail(key, formatPipelineDetailLabel(key), value);
    }
  }

  if (result) {
    if (typeof result.reason === 'string' && result.reason.trim()) {
      const reason = formatFineTuningJobErrorLine(result.reason.trim());
      if (reason && !parts.some((line) => trainingJobErrorsEquivalent(line, reason))) {
        parts.push(reason);
      }
    }
    if (typeof result.error === 'string' && result.error.trim()) {
      const err = formatFineTuningJobErrorLine(result.error.trim());
      if (err && !parts.some((line) => trainingJobErrorsEquivalent(line, err))) {
        parts.push(err);
      }
    }
    if (result.preview) {
      parts.push(`Preview:\n${formatPipelineDetailValue(result.preview)}`);
    }
    if (result.experiment) {
      parts.push(`Experiment:\n${formatPipelineDetailValue(result.experiment)}`);
    }
    if (Array.isArray(result.groups)) {
      handledDetailKeys.add('groups');
      const failedGroups = (result.groups as Array<{ status?: string; error?: string; base_model?: string }>)
        .filter((group) => String(group.status || '').toLowerCase() === 'failed' && String(group.error || '').trim())
        .map((group) => formatFineTuningJobErrorLine(String(group.error).trim()))
        .filter(Boolean);
      if (failedGroups.length > 0) {
        const block = dedupeTrainingJobErrorLines(failedGroups).join('\n\n');
        if (block && !parts.includes(block)) parts.push(block);
      }
    }
  }

  const taskError = (taskRun.error || '').trim();
  if (taskError) {
    const formatted = formatFineTuningJobErrorLine(taskError);
    if (formatted && !parts.some((line) => trainingJobErrorsEquivalent(line, formatted) || line.includes(taskError))) {
      parts.push(formatted);
    }
  }

  return parts.length > 0 ? parts.join('\n\n') : null;
}

export function findFailedPipelineTaskRun(
  taskRuns?: Array<{ status?: string }> | null
): { status?: string; type?: string; error?: string | null; result?: unknown } | null {
  if (!Array.isArray(taskRuns)) return null;
  return taskRuns.find((taskRun) => String(taskRun.status || '').toLowerCase() === 'failed') ?? null;
}

export function extractPipelineTaskExperimentId(taskRun: { type?: string; result?: unknown }): string | null {
  const type = String(taskRun.type || '').toLowerCase();
  if (type !== 'generate_vuser_datasets') return null;

  const result = normalizePipelineTaskResult(taskRun.result);
  const details = extractPipelineTaskResultDetails(result);
  const fromDetails = details && typeof details.experiment_id === 'string' ? details.experiment_id.trim() : '';
  if (fromDetails) return fromDetails;

  const experiment = result?.experiment;
  if (experiment && typeof experiment === 'object' && !Array.isArray(experiment)) {
    const id = (experiment as { id?: unknown }).id;
    if (typeof id === 'string' && id.trim()) return id.trim();
  }

  return null;
}

export function resolvePipelineTaskRunError(taskRun: {
  type?: string;
  error?: string | null;
  result?: unknown;
}): JobErrorDisplay {
  const type = String(taskRun.type || '').toLowerCase();
  const fallback =
    type === 'fine_tuning' || type === 'fine_tune'
      ? 'Fine-tuning failed. Review datasets, model settings, and API limits, then try again.'
      : type === 'generate_vuser_datasets'
        ? 'Virtual user dataset generation failed'
        : 'This pipeline step failed. Review the configuration and try again.';

  if (type === 'fine_tuning' || type === 'fine_tune') {
    const jobPayload = buildFineTuningErrorJobFromTaskRun(taskRun);
    const hasPayload = Boolean(
      String(jobPayload.error || '').trim() ||
      (Array.isArray(jobPayload.agents) && jobPayload.agents.length > 0) ||
      (Array.isArray(jobPayload.failed_agents) && jobPayload.failed_agents.length > 0) ||
      (Array.isArray(jobPayload.groups) && jobPayload.groups.length > 0)
    );
    if (hasPayload) {
      return resolveFineTuningJobError(jobPayload);
    }
  }

  const raw = extractPipelineTaskRunRawError(taskRun) || (taskRun.error || '').trim() || fallback;
  const friendly = resolvePipelineTaskRunFriendlyMessage(taskRun, fallback);
  const details = buildPipelineTaskRunErrorDetails(taskRun);
  const detailsText = (details || '').trim();
  const hasDetails =
    Boolean(detailsText) && normalizeJobErrorCompare(detailsText) !== normalizeJobErrorCompare(friendly);
  return {
    raw,
    friendly,
    hasDetails,
    details: hasDetails ? detailsText : undefined,
  };
}

export function resolvePipelineRunError(run: {
  error?: string | null;
  task_runs?: Array<{
    type?: string;
    status?: string;
    error?: string | null;
    result?: unknown;
  }>;
}): JobErrorDisplay {
  const fallback = 'Pipeline run failed. Review pipeline settings and try again.';
  const failedTask = findFailedPipelineTaskRun(run.task_runs);

  if (failedTask) {
    const taskDisplay = resolvePipelineTaskRunError(failedTask);
    const runRaw = extractPipelineErrorMessage((run.error || '').trim());
    return {
      raw: taskDisplay.raw || runRaw || fallback,
      friendly: taskDisplay.friendly,
      hasDetails: taskDisplay.hasDetails,
      details: taskDisplay.details,
    };
  }

  const raw = extractPipelineErrorMessage((run.error || '').trim());
  if (!raw) {
    return { raw: '', friendly: '', hasDetails: false };
  }
  const friendly = formatPipelineErrorMessage(raw, fallback);
  const hasDetails = normalizeJobErrorCompare(raw) !== normalizeJobErrorCompare(friendly);
  return { raw, friendly, hasDetails, details: hasDetails ? raw : undefined };
}

/**
 * Unifies training preview/job errors of the form
 * `AgentName: group base-model: reason: got N, need at least M`
 * into one readable line for notifications and inline preview.
 */
function formatTrainingExamplesShortageMessage(message: string): string | null {
  const m = message.trim();
  if (!m.toLowerCase().includes('not enough training examples')) return null;

  const gotNeed = m.match(/got\s+(\d+),\s*need at least\s+(\d+)/i);
  if (!gotNeed) return null;

  const got = gotNeed[1];
  const need = gotNeed[2];
  if (m.toLowerCase().includes('after conversion')) {
    return `Not enough training examples after conversion: found ${got}, need at least ${need}. Add more saved datasets or widen reaction filters.`;
  }
  return `Not enough training examples: found ${got}, need at least ${need}. Add more saved datasets or widen reaction filters.`;
}

export function formatFineTuningGroupErrorMessage(raw: string): string {
  const s = (raw || '').trim();
  if (!s) return s;

  const providerMessage = formatFineTuningProviderErrorMessage(s);
  if (providerMessage) return providerMessage;

  const m = s.match(/^(.+?):\s*group\s+(.+?):\s*(.+)$/i);
  if (m) {
    const agent = m[1].trim();
    const model = m[2].trim();
    let detail = m[3].trim();
    const detailProvider = formatFineTuningProviderErrorMessage(detail);
    if (detailProvider) return detailProvider;
    const shortage = formatTrainingExamplesShortageMessage(detail);
    if (shortage) {
      return `Group ${model} (${agent}): ${shortage}`;
    }
    detail = formatTrainingJobUserErrorMessage(detail);
    const gotNeed = detail.match(/^(.+?):\s*got\s+(\d+),\s*need at least\s+(\d+)\s*\.?\s*$/i);
    if (gotNeed) {
      let reason = gotNeed[1].trim().replace(/\.\s*$/, '');
      const got = gotNeed[2];
      const need = gotNeed[3];
      if (reason.length > 0) {
        reason = reason.charAt(0).toUpperCase() + reason.slice(1);
      }
      return `Group ${model} (${agent}): ${reason} — you have ${got}; at least ${need} required.`;
    }
    if (detail.length > 0) {
      detail = detail.charAt(0).toUpperCase() + detail.slice(1);
    }
    return `Group ${model} (${agent}): ${detail}`;
  }

  const groupOnly = s.match(/^group\s+(.+?):\s*(.+)$/i);
  if (groupOnly) {
    const model = groupOnly[1].trim();
    let detail = groupOnly[2].trim();
    const detailProvider = formatFineTuningProviderErrorMessage(detail);
    if (detailProvider) return detailProvider;
    const shortage = formatTrainingExamplesShortageMessage(detail);
    if (shortage) {
      return `Group ${model}: ${shortage}`;
    }
    detail = formatTrainingJobUserErrorMessage(detail);
    if (looksLikeTechnicalFineTuningError(detail)) {
      return formatFineTuningProviderErrorMessage(detail) ?? FINE_TUNING_GENERIC_USER_MESSAGE;
    }
    if (detail.length > 0) {
      detail = detail.charAt(0).toUpperCase() + detail.slice(1);
    }
    return `Group ${model}: ${detail}`;
  }

  const userFriendly = formatTrainingJobUserErrorMessage(s);
  if (userFriendly !== s) return userFriendly;

  if (looksLikeTechnicalFineTuningError(s)) {
    return formatFineTuningProviderErrorMessage(s) ?? FINE_TUNING_GENERIC_USER_MESSAGE;
  }

  return s;
}

/**
 * Converts technical fine-tuning dataset format errors into user-friendly guidance.
 * Keeps unknown errors unchanged.
 */
/** Maps known POST /training/jobs(/preview) error substrings to actionable UI copy. */
export function formatTrainingJobUserErrorMessage(message: string): string {
  const m = (message || '').trim();
  if (!m) return m;

  const providerMessage = formatFineTuningProviderErrorMessage(m);
  if (providerMessage) return providerMessage;

  const infrastructureMessage = formatFineTuningInfrastructureErrorMessage(m);
  if (infrastructureMessage) return infrastructureMessage;

  if (/^group\s+/i.test(m) || /^.+:\s*group\s+/i.test(m)) {
    const grouped = formatFineTuningGroupErrorMessage(m);
    if (grouped.trim()) return grouped;
  }

  const lower = m.toLowerCase();
  if (lower.includes('not enough training examples')) {
    const specific = formatTrainingExamplesShortageMessage(m);
    if (specific) return specific;
    return 'Not enough training examples for the selected datasets and filters. Select more saved datasets from the registry or widen reaction filters (for example include both like and dislike).';
  }
  if (lower.includes('dataset not found for integration')) {
    return 'One or more datasets are not available for this integration. Refresh the dataset list and ensure each selected dataset belongs to the current integration.';
  }
  if (lower.includes('agent not found in integration config')) {
    return 'An agent is missing from the current integration configuration. Reload the integration config from the server or adjust agent IDs, then try again.';
  }
  if (lower.includes('grouped fine-tuning requires one model_name per base_model group')) {
    return 'Grouped fine-tuning needs a model name for each base model group. Set a model name prefix in the Fine Tuning step and save the pipeline again.';
  }
  if (looksLikeTechnicalFineTuningError(m)) {
    return formatFineTuningProviderErrorMessage(m) ?? FINE_TUNING_GENERIC_USER_MESSAGE;
  }
  return m;
}

export interface TrainingJobFailedAgentLike {
  agent_name?: string;
  error?: string;
}

function normalizeTrainingJobErrorText(message: string): string {
  return formatTrainingJobUserErrorMessage(message).trim().toLowerCase();
}

function trainingJobErrorDisplayKey(message: string): string {
  return formatFineTuningGroupErrorMessage(formatTrainingJobUserErrorMessage(message.trim())).trim().toLowerCase();
}

/** Aligns `Agent: group model: detail` with bare `group model: detail`. */
function trainingJobErrorCoreKey(message: string): string {
  const trimmed = message.trim();
  if (!trimmed) return '';

  const agentGroupPrefix = trimmed.match(/^[^:]+:\s*(group\s+.+)$/i);
  if (agentGroupPrefix) {
    return agentGroupPrefix[1].trim().toLowerCase();
  }

  const formattedGroup = formatFineTuningGroupErrorMessage(trimmed);
  const parsed = formattedGroup.match(/^group\s+(.+?)\s*\([^)]+\):\s*(.+)$/i);
  if (parsed) {
    return `group ${parsed[1].trim()}: ${parsed[2].trim()}`.toLowerCase();
  }

  return trimmed.toLowerCase();
}

function trainingJobErrorsEquivalent(a: string, b: string): boolean {
  const left = a.trim();
  const right = b.trim();
  if (!left || !right) return false;
  if (trainingJobErrorDisplayKey(left) === trainingJobErrorDisplayKey(right)) return true;
  if (normalizeTrainingJobErrorText(left) === normalizeTrainingJobErrorText(right)) return true;
  const coreA = trainingJobErrorCoreKey(left);
  const coreB = trainingJobErrorCoreKey(right);
  return coreA.length > 0 && coreA === coreB;
}

function dedupeTrainingJobErrorLines(lines: string[]): string[] {
  const unique: string[] = [];
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    if (unique.some((existing) => trainingJobErrorsEquivalent(existing, trimmed))) continue;
    unique.push(trimmed);
  }
  return unique;
}

/** Lower score = higher priority when picking a single user-facing error. */
function fineTuningErrorPriorityScore(message: string): number {
  const lower = message.toLowerCase();
  if (lower.includes('agent is missing') || lower.includes('agent not found')) return 10;
  if (lower.includes('not available for this integration') || lower.includes('dataset not found')) return 12;
  if (lower.includes('model name for each base model')) return 15;
  if (lower.includes('not enough training examples')) return 20;
  if (lower.includes('openai api') || lower.includes('quota exceeded') || lower.includes('rate limit')) return 40;
  if (lower.includes('timed out') || lower.includes('could not reach your integration')) return 50;
  if (lower.includes('external ai provider error')) return 55;
  if (lower.includes('try again later or contact support')) return 90;
  return 60;
}

function pickPrimaryUserFacingErrorMessage(messages: string[]): {
  primary: string;
  additional: string[];
} {
  const unique = dedupeTrainingJobErrorLines(messages.map((message) => message.trim()).filter(Boolean));
  if (unique.length === 0) return { primary: '', additional: [] };
  if (unique.length === 1) return { primary: unique[0], additional: [] };
  const sorted = [...unique].sort(
    (left, right) => fineTuningErrorPriorityScore(left) - fineTuningErrorPriorityScore(right)
  );
  return { primary: sorted[0], additional: sorted.slice(1) };
}

function formatFineTuningJobErrorLine(rawLine: string): string {
  const trimmed = rawLine.trim();
  if (!trimmed) return '';
  return formatFineTuningGroupErrorMessage(formatTrainingJobUserErrorMessage(trimmed));
}

function splitCompositePipelineErrorMessage(message: string): string[] | null {
  const cleaned = message.replace(/\.{2,}/g, '.').trim();
  if (!cleaned) return null;
  const parts = cleaned
    .split(/\.\s+(?=Fine-tuning|Auto Fine-Tuning|An agent|Not enough|One or more|Grouped fine-tuning|Virtual user)/i)
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length <= 1) return null;
  return parts.map((part) => (part.endsWith('.') ? part : `${part}.`));
}

function formatTrainingJobFailedAgentLine(agent: TrainingJobFailedAgentLike): string {
  const agentName = String(agent.agent_name || '').trim();
  const errorRaw = String(agent.error || '').trim();
  if (!errorRaw) {
    return agentName ? `${agentName}: Unknown error` : 'Unknown error';
  }
  const formatted = formatFineTuningGroupErrorMessage(formatTrainingJobUserErrorMessage(errorRaw));
  if (agentName && !formatted.toLowerCase().includes(agentName.toLowerCase())) {
    return `${agentName}: ${formatted}`;
  }
  return formatted;
}

/**
 * Builds a user-facing training job error string from `message` and `failed_agents`.
 * When the top-level message duplicates an agent error, only the agent list is returned.
 */
export function formatTrainingJobFailedAgentsErrorMessage(params: {
  message?: string | null;
  failedAgents?: TrainingJobFailedAgentLike[] | null;
  fallbackMessage?: string;
  lineSeparator?: string;
}): string {
  const failedAgents = Array.isArray(params.failedAgents) ? params.failedAgents : [];
  const fallback = params.fallbackMessage ?? 'None of the selected agents could be previewed for fine-tuning.';
  const separator = params.lineSeparator ?? '\n\n';

  const agentLines = dedupeTrainingJobErrorLines(failedAgents.map(formatTrainingJobFailedAgentLine));

  if (agentLines.length === 0) {
    const main = String(params.message ?? '').trim();
    return formatTrainingJobUserErrorMessage(main || fallback);
  }

  const mainRaw = String(params.message ?? '').trim();
  if (!mainRaw) {
    return agentLines.join(separator);
  }

  const mainFormatted = formatFineTuningGroupErrorMessage(formatTrainingJobUserErrorMessage(mainRaw));

  const mainDuplicatesAgents =
    failedAgents.some((agent) => trainingJobErrorsEquivalent(mainRaw, String(agent.error || ''))) ||
    agentLines.some((line) => trainingJobErrorsEquivalent(mainRaw, line)) ||
    agentLines.some((line) => trainingJobErrorsEquivalent(mainFormatted, line));

  if (mainDuplicatesAgents) {
    return agentLines.join(separator);
  }

  const agentLinesWithoutMainDup = agentLines.filter((line) => !trainingJobErrorsEquivalent(mainFormatted, line));

  if (agentLinesWithoutMainDup.length === 0) {
    return mainFormatted;
  }

  return `${mainFormatted}${separator}${agentLinesWithoutMainDup.join(separator)}`;
}

export function formatFineTuningDatasetFormatErrorMessage(raw: string): string {
  const source = (raw || '').trim();
  if (!source) return source;
  const text = source.toLowerCase();

  const hasMissingAssistant =
    text.includes('no completion or assistant tokens') ||
    (text.includes('assistant') && (text.includes('not found') || text.includes('missing')));
  const hasContextOverflow =
    text.includes('outside of the context window') ||
    text.includes('context window') ||
    text.includes('truncation') ||
    text.includes('truncated');
  const hasInvalidJson =
    text.includes('invalid json') ||
    text.includes('json decode') ||
    text.includes('json parse') ||
    (text.includes('invalid file format') && text.includes('json'));

  if (!hasMissingAssistant && !hasContextOverflow && !hasInvalidJson) {
    return source;
  }

  const issues: string[] = [];
  if (hasMissingAssistant) {
    issues.push('In the dataset, assistant responses are missing (or not reachable after preprocessing).');
  }
  if (hasContextOverflow) {
    issues.push('Some dialogs exceed the allowed context window, so assistant parts may be cut off.');
  }
  if (hasInvalidJson) {
    issues.push('Dataset file has invalid JSON/JSONL structure.');
  }

  return [
    'Dataset format issue detected.',
    issues.join(' '),
    'Check that each sample contains both user and assistant messages, reduce dialog size, and validate JSON/JSONL format.',
    'Valid example: {"messages":[{"role":"user","content":"..."},{"role":"assistant","content":"..."}]}',
  ].join(' ');
}

/**
 * Reads a human-readable message from common API error body shapes (Axios response.data).
 */
export function extractHttpErrorBodyMessage(responseData: unknown): string | null {
  if (responseData == null) return null;
  if (typeof responseData === 'string') {
    const t = responseData.trim();
    return t || null;
  }
  if (typeof responseData !== 'object') return null;
  const o = responseData as Record<string, unknown>;

  const errObj = o.error;
  if (errObj && typeof errObj === 'object') {
    const msg = (errObj as Record<string, unknown>).message;
    if (typeof msg === 'string' && msg.trim()) return msg.trim();
  }
  if (typeof errObj === 'string' && errObj.trim()) return errObj.trim();

  if (typeof o.message === 'string' && o.message.trim()) return o.message.trim();

  const detail = o.detail;
  if (typeof detail === 'string' && detail.trim()) return detail.trim();
  if (Array.isArray(detail)) {
    const parts = detail
      .map((item: unknown) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object' && item !== null && 'msg' in item) {
          const msg = (item as { msg?: unknown }).msg;
          return typeof msg === 'string' ? msg : '';
        }
        return '';
      })
      .filter(Boolean);
    if (parts.length) return parts.join(' ');
  }

  const data = o.data;
  if (data && typeof data === 'object') {
    const inner = (data as Record<string, unknown>).message;
    if (typeof inner === 'string' && inner.trim()) return inner.trim();
  }

  return null;
}

export function getErrorMessage(error: any, defaultMessage: string = 'An error occurred. Please try again.'): string {
  if (!error) return defaultMessage;

  // Check if it's an axios error with response
  if (error.response) {
    const status = error.response.status;
    const responseData = error.response.data;

    let serverMessage: string | null = extractHttpErrorBodyMessage(responseData);

    if (
      status === 402 ||
      isAccountBalanceInsufficientTokensError(responseData) ||
      isAccountBalanceInsufficientTokensError(serverMessage)
    ) {
      return OPTIMIZATION_INSUFFICIENT_TOKENS_USER_MESSAGE;
    }

    const datasetMessage = formatInsufficientDatasetTokensMessage(responseData);
    if (datasetMessage) return datasetMessage;

    if (
      isInsufficientDatasetTokensForOptimizationError(responseData) ||
      isInsufficientDatasetTokensForOptimizationError(serverMessage)
    ) {
      return (
        formatInsufficientDatasetTokensMessage(responseData) ??
        formatInsufficientDatasetTokensMessage(serverMessage) ??
        'Not enough training data to start Config Tuning. Select additional datasets or increase the amount of training data, then try again.'
      );
    }

    // 429 daily rate limit (e.g. fine-tuning): show friendly message without technical details
    if (status === 429) {
      const code = responseData?.error?.code ?? responseData?.code;
      const isDailyLimit =
        code === 'daily_rate_limit_exceeded' ||
        (typeof serverMessage === 'string' && serverMessage.toLowerCase().includes('daily_rate_limit_exceeded'));
      if (isDailyLimit) {
        return 'Daily fine-tuning request limit reached (24 per day). Please try again later.';
      }
      if (!serverMessage) {
        return 'Too many requests. Please wait a moment and try again.';
      }
    }

    // If we have a server message, use it (capitalized)
    if (serverMessage) {
      return capitalizeError(serverMessage);
    }

    // Otherwise, provide user-friendly messages based on status code
    switch (status) {
      case 400:
        return 'Invalid request. Please check your input and try again.';
      case 401:
        return 'Session expired. Please log in again.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 409:
        return 'This action conflicts with the current state. Please refresh and try again.';
      case 422:
        return 'The data you provided is invalid. Please check your input.';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
        return 'Server error. Please try again later.';
      case 502:
        return 'Service temporarily unavailable. Please try again later.';
      case 503:
        return 'Service is temporarily unavailable. Please try again later.';
      case 504:
        return 'Request timeout. Please try again.';
      default:
        if (status >= 500) {
          return 'Server error. Please try again later.';
        } else if (status >= 400) {
          return 'Request failed. Please check your input and try again.';
        }
    }
  }

  // Check if it's a network error (no response)
  if (error.request && !error.response) {
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return 'Request timed out. Please check your connection and try again.';
    }
    if (error.message?.includes('Network Error')) {
      return 'Network error. Please check your internet connection and try again.';
    }
    return 'Unable to connect to the server. Please check your internet connection.';
  }

  // Check if it's a standard Error object
  if (error instanceof Error) {
    const payload = (error as Error & { optimizationErrorPayload?: unknown }).optimizationErrorPayload;
    const sources = resolveOptimizationErrorSources(error);

    for (const source of sources) {
      if (isAccountBalanceInsufficientTokensError(source)) {
        return OPTIMIZATION_INSUFFICIENT_TOKENS_USER_MESSAGE;
      }
    }

    for (const source of sources) {
      const datasetMessage = formatInsufficientDatasetTokensMessage(source);
      if (datasetMessage) return datasetMessage;
    }

    if (payload == null) {
      if (isAccountBalanceInsufficientTokensError(error.message)) {
        return OPTIMIZATION_INSUFFICIENT_TOKENS_USER_MESSAGE;
      }
      const datasetMessage = formatInsufficientDatasetTokensMessage(error.message);
      if (datasetMessage) return datasetMessage;
    }

    const message = error.message;

    // Filter out technical error messages
    if (message.includes('Request failed with status code')) {
      const statusMatch = message.match(/status code (\d+)/);
      if (statusMatch) {
        const status = parseInt(statusMatch[1], 10);
        return getErrorMessage({ response: { status, data: {} } }, defaultMessage);
      }
    }

    // If it's a generic axios error message, provide a better one
    if (message.includes('Network Error') || message.includes('timeout')) {
      return 'Connection error. Please check your internet connection and try again.';
    }

    // Otherwise, capitalize and return the message
    return capitalizeError(message) || defaultMessage;
  }

  // If error is a string
  if (typeof error === 'string') {
    return capitalizeError(error) || defaultMessage;
  }

  // Fallback to default message
  return defaultMessage;
}

/**
 * True when `fetch` to storage (presigned URL) likely failed on the transport layer
 * while the upload may still have completed (e.g. net::ERR_CONNECTION_CLOSED, "Failed to fetch").
 */
export function isLikelyTransientFetchError(err: unknown): boolean {
  if (err == null) return false;
  const name = err instanceof Error ? err.name : '';
  const msg = (err instanceof Error ? err.message : String(err)).toLowerCase();
  if (name === 'TypeError' && /fetch|network|load failed|failed to fetch|aborted/.test(msg)) return true;
  if (
    /failed to fetch|networkerror|net::err|connection (closed|reset)|load failed|econnreset|econnaborted|aborted|timeout/i.test(
      msg
    )
  ) {
    return true;
  }
  return false;
}
